import { useState } from "react";
import { Loading, Step, StepLabel, Stepper } from "../../components";
import { Minimal } from "../../layouts";
import * as XLSX from "xlsx";
import shortid from "shortid";
import { ImportarProdutoActions, ImportarProdutoStepper } from "./components";
import { InputErros } from "../../helpers";
import { Produtos } from "./entities";
import {
  CestService,
  NcmService,
  notification,
  ProdutosService,
} from "../../services";
import { responseErros } from "../../utils";
import produtosCsvValidator from "./middlewares/produtos-csv.validator";
import CodigoBarraRepetidosDialog from "./components/dialogs/codigo-barras-repetidos.component";

const ImportarProdutoView = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [arquivoCsv, setArquivoCsv] = useState(null);
  const [dadosCsv, setDadosCsv] = useState(null);
  const [isCadastro, setIsCadastro] = useState(false);
  const [
    openProdutosCodigosRepitidosDialog,
    setOpenProdutosCodigosRepitidosDialog,
  ] = useState(false);
  const [produtosCodigosRepitidos, setProdutosCodigosRepitidos] = useState([]);

  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const produtosService = new ProdutosService();
  const ncmService = new NcmService();
  const cestService = new CestService();

  const steps = [
    { titulo: "Selecionar Arquivo", funcaoAcao: buscarXmlData },
    { titulo: "Produtos", funcaoAcao: cadastrarProdutos },
  ];

  function extrairDadosProdutos(csvData) {
    const workbook = XLSX.read(csvData, { type: "buffer" });
    const planilhasValidas = ["PRODUTOS", "ANVISA", "COMBUSTIVEL"];
    let dataProdutos = [];
    for (const nomePlanilha of workbook.SheetNames) {
      if (!planilhasValidas.includes(nomePlanilha)) continue;
      const worksheet = workbook.Sheets[nomePlanilha];
      const data = XLSX.utils.sheet_to_json(worksheet);
      if (nomePlanilha === "PRODUTOS") {
        data.forEach((item) => {
          dataProdutos.push({
            ...item,
            id: shortid.generate(),
            ativado: true,
            tipo: "simples",
            fornecedores: [],
            produtosGrade: [],
          });
        });
        continue;
      }
      data.forEach((item, i) => {
        if (dataProdutos[i]) {
          dataProdutos[i] = { ...dataProdutos[i], ...item };
        }
      });
    }
    return dataProdutos?.length ? dataProdutos : [];
  }

  const handleFileRead = async (e) => {
    const csvData = e.target.result;
    const jsonData = extrairDadosProdutos(csvData);
    formatarProdutos(jsonData);
    setIsCadastro(true);
  };

  async function buscarXmlData() {
    try {
      if (!arquivoCsv) {
        throw new Error(
          "Por favor, inserir CSV/XLS referente a importação de produtos"
        );
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async (e) => {
          try {
            await handleFileRead(e);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () =>
          reject(new Error("Erro ao ler o arquivo CSV/XLS"));
        reader.readAsArrayBuffer(arquivoCsv);
      });
    } catch (error) {
      throw new Error(error.message);
    }
  }

  const validarCodigoBarras = async (dados = dadosCsv) => {
    const listCodigoBarras = dados
      .map((item) => item.codigoBarra)
      .filter((item) => typeof item === "string");
    if (new Set(listCodigoBarras).size !== listCodigoBarras.length) {
      const produtosRepetidos = dados.filter(
        (produto, index, self) =>
          self.findIndex((p) => p.codigoBarra === produto.codigoBarra) !== index
      );
      setProdutosCodigosRepitidos(produtosRepetidos);
      setOpenProdutosCodigosRepitidosDialog(true);
      return true;
    }
    return false;
  };

  async function cadastrarProdutos() {
    if (!dadosCsv?.length) {
      throw new Error(
        "Não há dados para importar. Por favor, verifique o arquivo CSV/XLS"
      );
    }
    produtosCsvValidator
      .validate(dadosCsv, { abortEarly: false })
      .then(async () => {
        setInputErro([]);
        setLoading(true);
        const codigoRepetido = await validarCodigoBarras();
        if (codigoRepetido) {
          setLoading(false);
          return;
        }
        const res = await produtosService.cadastrarListaProdutos(dadosCsv);
        setLoading(false);
        if (!res.isAxiosError) {
          notification.sucessoGenericos("Produtos importados com sucesso");
          handleInitStep();
        } else {
          if (
            res.response?.data?.message.includes("Códigos de barra existentes")
          ) {
            const codigosExistentes = res.response.data.message
              .replace("Códigos de barra existentes: ", "")
              .split(", ")
              .map((codigo) => codigo.trim());
            const produtosRepetidos = dadosCsv.filter((produto) =>
              codigosExistentes.includes(produto.codigoBarra)
            );
            setProdutosCodigosRepitidos(produtosRepetidos);
            setOpenProdutosCodigosRepitidosDialog(true);
          } else {
            throw new Error(res.response?.data?.message);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        inputErros.set(err);
        responseErros(err);
      });
  }

  async function formatarProdutos(dados) {
    setLoading(true);
    const ncmIdsProdutos = new Set();
    const cestIdsProdutos = new Set();
    dados.forEach((item) => {
      ncmIdsProdutos.add(parseInt(item.ncmId));
      cestIdsProdutos.add(parseInt(item.cestId));
    });
    const [listaNcm, listaCest] = await Promise.all([
      buscarNcms(Array.from(ncmIdsProdutos)),
      buscarCest(Array.from(cestIdsProdutos)),
    ]);
    const produtos = dados.map((item) => ({
      ...new Produtos(item, listaNcm, listaCest),
      id: shortid.generate(),
    }));
    setDadosCsv(produtos);
    console.log(produtos);
  }

  const buscarNcms = async (ids) => {
    const filtros = {
      id: ids,
      nonPaginated: true,
    };
    if (ids.length) {
      const result = await ncmService.getAllAvancado(filtros);
      if (!result.isAxiosError) {
        return result.data;
      } else {
        responseErros(result);
        throw new Error(
          result.response?.data?.message || "Erro ao buscar NCMs"
        );
      }
    }
  };

  const buscarCest = async (ids) => {
    const filtros = {
      id: ids,
      nonPaginated: true,
    };
    if (ids.length) {
      const result = await cestService.getAll(filtros);
      if (!result.isAxiosError) {
        return result.data;
      } else {
        responseErros(result);
        throw new Error(
          result.response?.data?.message || "Erro ao buscar CESTs"
        );
      }
    }
  };

  const handleInitStep = () => {
    setActiveStep(0);
    setArquivoCsv(null);
    setDadosCsv(null);
    setIsCadastro(false);
    setInputErro([]);
  };

  return (
    <>
      <Minimal
        title="Importação de produtos"
        cardAction={
          <ImportarProdutoActions
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handleInitStep={handleInitStep}
            isCadastro={isCadastro}
            setIsCadastro={setIsCadastro}
            setLoading={setLoading}
          />
        }
      >
        <Stepper activeStep={activeStep}>
          {steps.map(({ titulo }) => (
            <Step key={titulo}>
              <StepLabel>{titulo}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <ImportarProdutoStepper
          activeStep={activeStep}
          arquivoCsv={arquivoCsv}
          setArquivoCsv={setArquivoCsv}
          dadosCsv={dadosCsv}
          setIsCadastro={setIsCadastro}
          inputErros={inputErros}
        />
        <CodigoBarraRepetidosDialog
          open={openProdutosCodigosRepitidosDialog}
          setOpen={setOpenProdutosCodigosRepitidosDialog}
          produtosCodigosRepitidos={produtosCodigosRepitidos}
          handleInitStep={handleInitStep}
        />
      </Minimal>
      <Loading loading={loading} />
    </>
  );
};

export default ImportarProdutoView;
